import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {Range, Coordinates, intersection, Rect, SnapCoordinates} from "@/event/calendar/Shapes.ts";
import type {Animation, AnimationID} from "@/event/calendar/dom/Animation.ts";
import {CustomEvent} from "@/event/calendar/dom/CustomEvent.ts";
import {CanvasRootComponent} from "@/event/calendar/dom/CanvasRootComponent.ts";
import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {anchorMouseEnter, anchorMouseLeave} from "@/utils/WebPreview.ts";

/**
 * How layout steps work:
 * Method layout(dc) is called from top to bottom (CavasRootComponent first)
 *
 * 1. Parent will calculate its content-box (calculateContentBox). Most of the components
 *    use the whole available whidth and height (this.x, this.y, this.width, this.height)
 *    but some components like FrameComponent don't. Content box is sub-area where children
 *    can be placed (if other then full width x height).
 * 2. Parent's placeChildren(dc, contentBox) is called. This method sets maximum allowed
 *    dimensions for each child and initial position. Default implementation sets maximum
 *    to the full content box and top left the same as content box.
 * 3. Parent loops over its children, where for each calls layout(dc). Children goes through
 *    the same process as its parent.
 * 4. Finally parent's layoutSelf(dc, contentBox) is called to make final decisions.
 *    Parent should set its final width and height and distribute children according to
 *    their "fit-content" dimensions
 *    - set maximum allowed width and height of the child
 *    - let child do the full layout procedure for itself
 */

export interface TargetPosition {
	target: Component;
	/* Pointer position relative to the target's top,left corner */
	x: number;
	y: number;
}

export const BUBBLE:boolean = true;
export const DONE:boolean = false;

export const TOUCH:boolean = navigator.maxTouchPoints > 0;

/**
 * Represent canvas component (equivalent of DOM element for HTML)
 */
export class Component {
	parent: Component = null;
	children?: Component[];

	/** Offset of this component from its parent's left edge */
	x: number;
	/** Offset of this component from its parent's top edge */
	y: number;
	/** Offset of baseline of the first line from top edge of the component */
	baseline: number;

	/** Calculated width and height of the component */
	width: number;
	height: number;

	/** If true, drawing will be clipped to component's rectangle (like overflow:hidden in CSS) */
	clip: boolean;

	/** Cursor of this component */
	public cursor: string|null|undefined;

	constructor() {
	}

	get mounted():boolean {
		return this.parent != null;
	}

	/** Return component's cursor for given coordinates */
	getCursor(x:number, y: number) {
		return this.cursor;
	}

	append(child:Component): Component {
		if (!this.children) this.children = [];
		if (child.mounted) child.remove(); // Must be removed first
		this.children.push(child);
		child.parent = this;
		invokeTopDown(child, (c) => c.afterMounted());
		this.dirty(true);
		return this;
	}


	prepend(child:Component): Component {
		if (!this.children) this.children = [];
		if (child.mounted) child.remove(); // Must be removed first
		this.children.splice(0, 0, child);
		child.parent = this;
		invokeTopDown(child, (c) => c.afterMounted());
		this.dirty(true);
		return this;
	}

	/** Remove given child from children array */
	private removeChild(child:Component) {
		if (this.children) {
			const idx = this.children.findIndex((c:Component) => c === child);
			if (idx >= 0) this.children.splice(idx, 1);
			this.dirty(true);
		}
	}

	/** Remove this component from parent component, all children are removed to */
	remove():void {
		if (this.parent) {
			invokeBottomUp(this, (c) => c.beforeUnmounted());
			this.parent.removeChild(this);
			this.parent = null;
		}
	}

	/* 1. */
	/** Chance to reduce content area available to children (e.g. for border) */
	calculateContentBox(contentWidth: number, contentHeight: number):Rect {
		return { x: 0, y: 0, width: contentWidth, height: contentHeight };
	}

	/* 2. */
	/** Apply constraints to children */
	placeChildren(dc: DrawingContext, contentBox:Rect) {
		// Default implementation will allow all children to cover the whole content box
		// which is probably not what you want.
		if (this.children) {
			for (let child of this.children) {
				child.x = contentBox.x;
				child.y = contentBox.y;
				child.width = contentBox.width;
				child.height = contentBox.height;
			}
		}
	}


	/* 3. */
	/** Calculate positions for only this component. All children are already laid out */
	layoutSelf(dc: DrawingContext, contentBox: Rect) {
		// You should override this method
	}


	/**
	 * Calculate positions
	 * @param dc drawing context
	 * @param containerWidth total available width (if <= 0 it means it is unconstrained)
	 * @param containerHeight total available height (if <= 0 it means it is unconstrained)
	 **/
	layout(dc: DrawingContext) {
		// this component should have set width x height by its parent as maximum allowed dimentions
		// 1. We get content box of this component
		const contentBox = this.calculateContentBox(this.width, this.height);
		// 2. Apply constraints to children
		this.placeChildren(dc, contentBox);

		// Layout all children
		if (this.children) {
			for (let child of this.children) {
				child.layout(dc);
			}
		}
		this.layoutSelf(dc, contentBox);
	}

	draw(dc:DrawingContext, area:Rect) {
		if (this.clip) {
			dc.ctx.save();
			dc.ctx.beginPath();
			dc.ctx.rect(0, 0, this.width, this.height);
			dc.ctx.clip();
			//onsole.log("Clipped to " + this.width + " x " + this.height);
		}
		this.drawBackground(dc, area);
		this.drawBorder(dc, area);
		this.drawChildren(dc, area);
		this.drawContent(dc, area);
		if (this.clip) {
			dc.ctx.restore();
		}
	}

	protected drawBackground(dc: DrawingContext, area:Rect) {
	}

	protected drawBorder(dc: DrawingContext, area:Rect) {
	}

	protected drawChildren(dc: DrawingContext, area:Rect) {
		if (this.children) {
			for (let child of this.children) {
				try {
					dc.ctx.save();
					const toUpdate = intersection(area, { x: child.x, y: child.y, width: child.width, height: child.height});
					dc.ctx.translate(child.x, child.y);	// child will have different coordinates
					toUpdate.x = 0;
					toUpdate.y = 0;
					child.draw(dc, toUpdate);
				} finally {
					dc.ctx.restore();
				}
			}
		}
	}

	protected drawContent(dc: DrawingContext, area:Rect) {
	}

	/**
	 * Mark this component as dirty for laying out (layout=true) and painting
	 * @param layout set true if the component changed such that layout recalculation is required
	 */
	dirty(layout:boolean) {
		if (this.parent) {
			this.parent.dirty(layout);
		}
	}

	/** Lifecycle hook invoked when this component became part of component tree */
	afterMounted(): void {
	}

	/** Lifecycle hook invoked when this component is going to be removed from component tree */
	beforeUnmounted(): void {
	}

	/**
	 * Gets coordinates relative to this parent and returns coordinates local to a child (from parameter).
	 * Call on parent
	 */
	parentToChild(child:Component, x: number, y: number):Coordinates {
		return { x: x - child.x, y: y - child.y};
	}

	/**
	 * Gets coordinates relative to this child and returns coordinates relative to the parent
	 */
	childToParent(child:Component, x: number, y: number):Coordinates {
		return { x: x + child.x, y: y + child.y};
	}

	/**
	 * Find target component based on local x,y mouse coordinates.
	 * Found component will be at the end of returned array. Root component will be at idx 0
	 */
	findTarget(x: number, y: number):TargetPosition[] {
		if (this.children) {
			for (let i = this.children.length - 1; i >= 0; i--) {
				const child = this.children[i];
				if (isInside(x, y, child)) {
					const childCoord = this.parentToChild(child, x, y);
					let childPath = child.findTarget(childCoord.x, childCoord.y);
					childPath.splice(0, 0, { target: this, x, y });
					return childPath;
				}
			}
		}
		return [{ target: this, x, y }];
	}

	onWheel(e: WheelEvent, localX:number, localY:number) {
		return BUBBLE;
	}

	onPointerMove(e: PointerEvent, localX: number, localY: number) {
		return BUBBLE;
	}

	onClick(e: MouseEvent, localX:number, localY: number) {
		return BUBBLE;
	}

	animate(a: Animation):AnimationID {
		if (this.parent == null) return 0;	// ignore.. component is not connected to DOM
		return this.parent.animate(a);		// pass the animation to parent
	}

	cancelAnimation(id: AnimationID) {
		if (this.parent == null) return;
		this.parent.cancelAnimation(id);
	}

	/**
	 * Return coordinates to snap scrolling to or null if snapping not supported.
	 */
	getSnapPosition(x: number, y: number, xPositiveDir?:boolean, yPositiveDir?:boolean):SnapCoordinates | null {
		return null;
	}

	onPointerLeave(e: PointerEvent, x: number, y: number) {
		this.cursorOutOfPreviewObject();
		return BUBBLE;
	}

	onPointerEnter(e: PointerEvent, x: number, y: number) {
		return BUBBLE;
	}

	onEvent(e: CustomEvent):boolean {
		return BUBBLE;
	}

	fireEvent(name:string, props: { [key:string]:any}):void {
		const e:CustomEvent = {
			...props,
			name,
			target: this
		}
		let component:Component = this;
		do {
			if (!component.onEvent(e)) break;
			component = component.parent;
		} while (component != null);
	}

	componentOfClass<T extends Component>(component: { new (...args: any[]): T }): T | undefined {
		if (this instanceof component) return this as T;
		if (this.children) {
			for (let child of this.children) {
				const found = child.componentOfClass(component);
				if (found) return found;
			}
		}
		return undefined;
	}

	onPointerUp(e: PointerEvent, x: number, y: number) {
		return BUBBLE;
	}

	onPointerDown(e: PointerEvent, x: number, y: number) {
		return BUBBLE;
	}

	setPointerCapture(e: PointerEvent):void {
		this.rememberPointerCapture(e, this);
	}

	/* Sending capture to the root element */
	protected rememberPointerCapture(e:PointerEvent, target:Component) {
		if (this.parent) this.parent.rememberPointerCapture(e, target);
	}

	horizontalScrollRange():Range {
		return {};	// unlimited
	}

	verticalScrollRange():Range {
		return {};	// unlimited
	}

	/**
	 * Called by ScrollComponent to notify children that scrolling ended and is calm now. Component
	 * can use this call to do some clean-up or state storage.
	 */
	scrollIdle() {

	}

	/**
	 * If this component is embedded in ScrollComponent it gets a chance to influence
	 * initial scroll values. The default behavior is that SC sets itself to
	 * scrollX=0, scrollY=0 which may not be desired behaviour.
	 */
	initialScroll():{ x?:number, y?:number }|null {
		return null;
	}

	root():CanvasRootComponent {
		let c:Component = this;
		while (!(c instanceof CanvasRootComponent || c == null)) c = c.parent;
		return c as CanvasRootComponent;
	}

	onDragStart(e: DragEvent, x: number, y: number) {
		this.cursorOutOfPreviewObject();
		return BUBBLE;
	}

	onDragEnd(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	onDrag(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	onDragEnter(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	onDragLeave(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	onDragOver(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	onDrop(e: DragEvent, x: number, y: number) {
		return BUBBLE;
	}

	setCursorForDragEffect(effect:string) {
		const oldCursor = this.cursor;
		if (effect) this.cursor = effect;
		else this.cursor = null;
		if (this.cursor !== oldCursor) this.dirty(false);	// the cursor can control how drop place is rendered (for this reason the dirty is here)
	}

	onTouchStart(e: TouchEvent, x: number, y: number) {
		return BUBBLE;
	}

	onTouchMove(e: TouchEvent, x: number, y: number) {
		return BUBBLE;
	}

	onTouchEnd(e: TouchEvent, x: number, y: number) {
		return BUBBLE;
	}

	toRootCoordinates(x:number, y:number):Coordinates {
		let point:Coordinates = { x, y };
		let child:Component = this;
		for (let p = this.parent; p != null ; child = p, p = p.parent) {
			point = p.childToParent(child, point.x, point.y);
		}
		return point;
	}

	private insidePreviewObject:string = null;

	cursorInPreviewObject(r:Rect, previewKey:string) {
		if (previewKey === this.insidePreviewObject) return;	// already entered
		if (this.insidePreviewObject) anchorMouseLeave();	// there was a different key but in the same component (we need to leave it)
		this.insidePreviewObject = previewKey;

		const canvasCoord = this.toRootCoordinates(r.x, r.y);
		const rt = this.root() as CanvasRootComponent;
		const canvas = rt.canvas;
		const cm = getElementMeasure(canvas);
		const m = calculateElementMeasure(cm, { x: canvasCoord.x, y: canvasCoord.y, width: r.width, height: r.height });

		anchorMouseEnter(null, {
			element: canvas,
			key: previewKey,
			measure: m
		});
	}

	cursorOutOfPreviewObject() {
		if (this.insidePreviewObject) {
			this.insidePreviewObject = null;
			anchorMouseLeave();
		}
	}
}

function calculateElementMeasure(canvas: ElementMeasure, elr: Rect):ElementMeasure {
	const m:ElementMeasure = { ...canvas };
	m.viewLeft += elr.x;
	m.viewTop += elr.y;
	m.left += elr.x;
	m.top += elr.y;
	m.width = elr.width;
	m.height = elr.height;
	m.spaceLeft += elr.x;
	m.spaceTop += elr.y;
	m.spaceRight += canvas.width - elr.x - elr.width;
	m.spaceBottom += canvas.height - elr.y - elr.height;
	return m;
}


export function isInside(x: number, y: number, c: Component|Rect): boolean {
	return x >= c.x && y >= c.y && x < (c.x + c.width) && y < (c.y + c.height);
}

/** Traverse tree of components starting from the root and invoke given method on each component (leaf component first) */
function invokeBottomUp(component: Component, method: (c: Component) => void): void {
	if (component.children && component.children.length > 0) {
		// Children first
		for (let child of component.children) {
			invokeBottomUp(child, method);
		}
	}
	method(component);
}

/** Traverse tree of components starting from the root and invoke given method on each component (root component first) */
function invokeTopDown(component: Component, method: (c: Component) => void): void {
	method(component);
	if (component.children && component.children.length > 0) {
		// Children last
		for (let child of component.children) {
			invokeTopDown(child, method);
		}
	}
}

// @ts-ignore
export const isMac = navigator.platform.toUpperCase().includes('MAC') || (navigator.userAgentData && navigator.userAgentData.platform === 'macOS');

export function isCopyModifier(e: DragEvent): boolean {
	return isMac ? e.altKey : e.ctrlKey;
}